import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import {
  Typography, Button, Table, Tag, Space, Drawer, Form, Input,
  Select, message, Popconfirm, Segmented, Badge,
} from 'antd';
import { DeleteOutlined, FileTextOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';

let _cache = null;

const SESSION_STATUS_OPTIONS = [
  { label: 'Pending',     value: 'pending'     },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Complete',    value: 'complete'    },
];

const STATUS_COLOR = { assigned: 'blue', in_progress: 'orange', completed: 'green' };
const STATUS_LABEL = { assigned: 'Assigned', in_progress: 'In Progress', completed: 'Completed' };

function sessionStatus(r) {
  const done  = r.CompletedCount ?? 0;
  const total = r.TotalEvaluators ?? 0;
  if (total === 0)        return { label: 'Pending',     color: 'default' };
  if (done === total)     return { label: 'Complete',    color: 'green'   };
  if (done > 0)           return { label: 'In Progress', color: 'orange'  };
  return                         { label: 'Pending',     color: 'blue'    };
}

export default function CalibrationPage() {
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);
  const isAdmin   = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  const canCreate = (currentUser?.CanEditCoaching ?? 0) >= 2;

  const restoredRef = useRef(!!_cache);
  const [sessions, setSessions]         = useState(() => _cache?.sessions ?? []);
  const [loading, setLoading]           = useState(false);
  const [filter, setFilter]             = useState('mine');
  const [statusFilter, setStatusFilter]   = useState(() => _cache?.statusFilter   ?? ['pending', 'in_progress']);
  const [campaignFilter, setCampaignFilter] = useState(() => _cache?.campaignFilter ?? null);
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [campaigns, setCampaigns]           = useState([]);
  const [forms, setForms]                   = useState([]);
  const [formsLoading, setFormsLoading]     = useState(false);
  const [evaluators, setEvaluators]         = useState([]);

  const [form] = Form.useForm();

  const loadSessions = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter.length) params.status = statusFilter.join(',');
    if (campaignFilter)      params.campaign_id = campaignFilter;
    api.get('/api/calibration/sessions', { params })
      .then((r) => {
        setSessions(r.data);
        _cache = { sessions: r.data, statusFilter, campaignFilter };
      })
      .catch(() => message.error('Failed to load calibration sessions.'))
      .finally(() => setLoading(false));
  }, [statusFilter, campaignFilter]);

  useEffect(() => {
    restoredRef.current = false;
    api.get('/api/agent-stats/options')
      .then((r) => setCampaigns(r.data.campaigns || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const myPending = useMemo(
    () => sessions.filter((s) => s.MyCaseID && s.MyStatus !== 'completed'),
    [sessions],
  );

  const displayedSessions = useMemo(
    () => filter === 'mine' ? myPending : sessions,
    [filter, sessions, myPending],
  );

  const openDrawer = () => {
    setEvaluators([]);
    setForms([]);
    setDrawerOpen(true);
  };

  const onCampaignChange = (campaignId) => {
    form.setFieldValue('form_id', null);
    form.setFieldValue('evaluator_ids', []);
    setForms([]);
    setEvaluators([]);
    if (!campaignId) return;
    // Load evaluators scoped to this campaign, and forms for it
    api.get('/api/users', { params: { can_edit_evals: 1, campaign_id: campaignId } })
      .then((r) => setEvaluators(r.data.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))))
      .catch(() => {});
    setFormsLoading(true);
    api.get('/api/eval-forms/published', { params: { campaign_id: campaignId } })
      .then((r) => setForms(r.data.map((f) => ({ label: `${f.Name} v${f.Version}`, value: f.ID }))))
      .catch(() => {})
      .finally(() => setFormsLoading(false));
  };

  const handleCreate = async (values) => {
    if (!values.evaluator_ids || values.evaluator_ids.length < 2) {
      message.error('Select at least 2 evaluators.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/calibration/sessions', {
        title:         values.title,
        contact_id:    values.contact_id,
        campaign_id:   values.campaign_id,
        form_id:       values.form_id,
        evaluator_ids: values.evaluator_ids,
      });
      message.success('Calibration session created.');
      form.resetFields();
      setDrawerOpen(false);
      loadSessions();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create session.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/calibration/sessions/${id}`);
      message.success('Session deleted.');
      loadSessions();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to delete session.');
    }
  };

  const columns = [
    {
      title: 'Created',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 155,
      render: (v) => formatTS(v),
    },
    {
      title: 'Title',
      dataIndex: 'Title',
      key: 'Title',
      render: (title, r) => (
        <Typography.Link onClick={(e) => { e.stopPropagation(); navigate(`/calibration/${r.ID}`); }}>
          {title}
        </Typography.Link>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'ContactID',
      key: 'ContactID',
      width: 110,
      render: (v) => v ?? '—',
    },
    {
      title: 'Campaign',
      dataIndex: 'Campaign',
      key: 'Campaign',
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Form',
      key: 'Form',
      ellipsis: true,
      render: (_, r) => r.FormName ? `${r.FormName} v${r.FormVersion}` : '—',
    },
    {
      title: 'Evaluators',
      dataIndex: 'EvaluatorNames',
      key: 'EvaluatorNames',
      render: (v) => v ?? '—',
    },
    {
      title: 'Status',
      key: 'Status',
      width: 120,
      render: (_, r) => {
        const s = sessionStatus(r);
        return (
          <Space size={4} direction="vertical" style={{ gap: 2 }}>
            <Tag color={s.color}>{s.label}</Tag>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {r.CompletedCount ?? 0} / {r.TotalEvaluators ?? 0} complete
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: 'My Assignment',
      key: 'MyAssignment',
      width: 160,
      render: (_, r) => {
        if (!r.MyCaseID) return <Typography.Text type="secondary">—</Typography.Text>;
        return (
          <Space size={6}>
            <Tag color={STATUS_COLOR[r.MyStatus] ?? 'default'}>
              {STATUS_LABEL[r.MyStatus] ?? r.MyStatus}
            </Tag>
            {r.MyStatus !== 'completed' && (
              <Button
                size="small"
                type="link"
                icon={<FileTextOutlined />}
                style={{ padding: 0 }}
                onClick={(e) => { e.stopPropagation(); navigate(`/cases/${r.MyCaseID}`); }}
              >
                Open
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'CreatedByName',
      key: 'CreatedByName',
      width: 140,
    },
    ...(isAdmin ? [{
      title: '',
      key: 'actions',
      width: 56,
      render: (_, r) => (
        <Popconfirm
          title="Delete this calibration session?"
          description="All evaluations in this session will also be deleted."
          onConfirm={(e) => { e?.stopPropagation(); handleDelete(r.ID); }}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Calibration Sessions</Typography.Title>
        <Space wrap>
          <Select
            allowClear placeholder="All campaigns"
            showSearch optionFilterProp="label"
            options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
            value={campaignFilter}
            onChange={setCampaignFilter}
            style={{ minWidth: 180 }}
          />
          <Select
            mode="multiple" allowClear placeholder="All statuses"
            options={SESSION_STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            maxTagCount="responsive"
            style={{ minWidth: 200 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={loadSessions} loading={loading}>Search</Button>
        </Space>
      </div>

      <Segmented
        value={filter}
        onChange={(v) => { setFilter(v); loadSessions(); }}
        style={{ marginBottom: 16 }}
        options={[
          {
            label: (
              <Badge count={myPending.length} size="small" offset={[6, -2]}>
                <span style={{ paddingRight: myPending.length > 0 ? 10 : 0 }}>My Pending</span>
              </Badge>
            ),
            value: 'mine',
          },
          { label: 'All Sessions', value: 'all' },
        ]}
      />

      <Table
        rowKey="ID"
        dataSource={displayedSessions}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} sessions` }}
        locale={{
          emptyText: filter === 'mine'
            ? 'No pending calibration assignments.'
            : 'No calibration sessions yet.',
        }}
        onRow={(r) => ({ onClick: () => navigate(`/calibration/${r.ID}`), style: { cursor: 'pointer' } })}
      />

      {/* Drawer is available for future use — button is intentionally hidden */}
      {canCreate && (
        <Drawer
          title="New Calibration Session"
          open={drawerOpen}
          onClose={() => { setDrawerOpen(false); form.resetFields(); }}
          width={480}
          destroyOnClose
          footer={
            <Space style={{ justifyContent: 'flex-end', display: 'flex' }}>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={() => form.submit()}>Create</Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item name="title" label="Session Title" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="e.g. May 2026 Calibration" />
            </Form.Item>

            <Form.Item name="campaign_id" label="Campaign" rules={[{ required: true, message: 'Required' }]}>
              <Select
                showSearch optionFilterProp="label" allowClear
                placeholder="Select a campaign…"
                options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
                onChange={onCampaignChange}
                onClear={() => onCampaignChange(null)}
              />
            </Form.Item>

            <Form.Item name="contact_id" label="Contact ID" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="Enter contact / call ID" />
            </Form.Item>

            <Form.Item name="form_id" label="Eval Form" rules={[{ required: true, message: 'Required' }]}>
              <Select
                showSearch optionFilterProp="label" loading={formsLoading}
                placeholder={form.getFieldValue('campaign_id') ? 'Select form…' : 'Select a campaign first…'}
                options={forms}
                disabled={!form.getFieldValue('campaign_id')}
              />
            </Form.Item>

            <Form.Item name="evaluator_ids" label="Evaluators (min 2)" rules={[{ required: true, message: 'Required' }]}>
              <Select
                mode="multiple" showSearch optionFilterProp="label" maxTagCount="responsive"
                placeholder={form.getFieldValue('campaign_id') ? 'Select evaluators…' : 'Select a campaign first…'}
                options={evaluators}
                disabled={!form.getFieldValue('campaign_id')}
              />
            </Form.Item>
          </Form>
        </Drawer>
      )}
    </>
  );
}
